import { ColDef, PostSortRowsParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useCallback, useMemo, useRef } from "react";
import { aggridHeaderClass, aggridSwitchButtonWrapperStyle, SmallTitleWithSubSpan } from "src/components/common/common";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import {
    CreateCellClickHandler, CustomCellClickAction, createCellCssClassRules, createGridDropDownCommitAfter,
    getSwitchButtonColumnOptions, GridCellConfig, postSortHandler, RemoveRowHandler, useRowSelectionHandler,
} from "src/utils/grid/GridHandlerContent";
import { CommonCodeColumn } from "src/utils/grid/GridColumnContent";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags } from "src/utils/grid/gridStyleUtils";
import { showWarning } from "src/utils/toast/toastUtil";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { HRM3110_R01Dto } from "./Dto_HRM3110_R01";
import { useHRM3110_R01Store } from "./Store_HRM3110_R01";
import {
    allowHeaderNames_HRM3110_R01, deductHeaderNames_HRM3110_R01, HRM3110_R01GridType,
    isSystemAllowDeductCode, TAX_TYPE_NONTAX,
} from "./Util_HRM3110_R01";
import { useHRM3110_R01CellChange } from "./hooks/HRM3110_R01CellChange";

// 그리드별 컬럼 배치·너비를 브라우저에 보관할 때 사용하는 키이다. 두 그리드의 컬럼 구성이 달라 키를 나눈다.
const ALLOW_COLUMN_STATE_KEY = 'HRM3110_R01-allow-grid-column-state';
const DEDUCT_COLUMN_STATE_KEY = 'HRM3110_R01-deduct-grid-column-state';

/** Container에서 전달받는 값의 모양이다. */
interface Props {
    gridType: HRM3110_R01GridType; // 'allow'이면 지급항목, 'deduct'이면 공제항목 그리드를 그린다.
    clickable: boolean; // true이면 행 클릭을 선택으로 처리한다. +/- 버튼 직후에는 false가 되어 같은 클릭을 한 번 무시한다.
    setClickable: (clickable: boolean) => void; // clickable 값을 바꾸는 함수
    gridRef: React.RefObject<AgGridReact<HRM3110_R01Dto> | null>; // 이 그리드 인스턴스를 가리키는 ref. 편집 확정·스크롤 이동에 사용한다.
    defaultHRM3110_R01Row: HRM3110_R01Dto; // 이 그리드의 신규 행과 빈 행을 만들 때 쓰는 기본값
    handleInsertClick: (event: React.MouseEvent, rowIndex?: number, gridType?: HRM3110_R01GridType) => void; // 행 추가 함수. rowIndex가 있으면 해당 행 아래에 추가한다.
}

/**
 * 지급 또는 공제 항목을 편집 가능한 그리드로 렌더링한다. 같은 컴포넌트를 gridType만 바꿔 두 번 사용한다.
 * @param gridType 그릴 그리드 종류. 컬럼 구성과 사용할 Store 상태를 정한다.
 * @param gridRef Content와 공유하여 편집 확정 및 행 이동에 사용한다.
 * @param defaultHRM3110_R01Row 신규 행과 그리드 기본 행의 초기값이다.
 * @returns 컬럼 상태, 선택·삭제 표시, 셀 편집 이벤트가 연결된 그리드 JSX
 */
export const Table_HRM3110_R01 = (props: Props) => {

    // 구조 분해: props 객체에서 필요한 값을 각각의 변수로 꺼낸다.
    const {
        gridType, clickable, setClickable, gridRef, defaultHRM3110_R01Row, handleInsertClick,
    } = props;
    // true이면 지급항목 그리드이다. 컬럼 구성과 Store 상태 선택에 쓴다.
    const isAllowGrid = gridType === 'allow';

    // 화면 Store 전체에서 이 그리드에 해당하는 상태와 함수를 아래에서 골라 쓴다.
    const store = useHRM3110_R01Store();
    // 삼항 연산자: 지급 그리드이면 Allow 상태를, 공제 그리드이면 Deduct 상태를 이 그리드의 목록·함수로 사용한다.
    const gridList = isAllowGrid ? store.HRM3110_R01AllowList : store.HRM3110_R01DeductList;
    const setGridList = isAllowGrid ? store.setHRM3110_R01AllowList : store.setHRM3110_R01DeductList;
    const removeGridRow = isAllowGrid ? store.removeHRM3110_R01AllowList : store.removeHRM3110_R01DeductList;
    const updateGridRow = isAllowGrid ? store.updateHRM3110_R01AllowList : store.updateHRM3110_R01DeductList;
    const selectedList = isAllowGrid ? store.selectedHRM3110_R01AllowList : store.selectedHRM3110_R01DeductList;
    const addSelected = isAllowGrid ? store.addSelectedHRM3110_R01AllowList : store.addSelectedHRM3110_R01DeductList;
    const removeSelected = isAllowGrid ? store.removeSelectedHRM3110_R01AllowList : store.removeSelectedHRM3110_R01DeductList;
    const initSelected = isAllowGrid ? store.initSelectedHRM3110_R01AllowList : store.initSelectedHRM3110_R01DeductList;
    const allSelected = isAllowGrid ? store.allSelectedHRM3110_R01AllowList : store.allSelectedHRM3110_R01DeductList;
    const deletedList = isAllowGrid ? store.deletedHRM3110_R01AllowList : store.deletedHRM3110_R01DeductList;
    const addDeleted = isAllowGrid ? store.addDeletedHRM3110_R01AllowList : store.addDeletedHRM3110_R01DeductList;
    const originalList = isAllowGrid ? store.originalHRM3110_R01AllowList : store.originalHRM3110_R01DeductList;
    const columnState = isAllowGrid ? store.allowColumnState : store.deductColumnState;
    const setColumnState = isAllowGrid ? store.setAllowColumnState : store.setDeductColumnState;
    const scrollTop = isAllowGrid ? store.allowScrollTop : store.deductScrollTop;
    const setScrollTop = isAllowGrid ? store.setAllowScrollTop : store.setDeductScrollTop;
    const headerNames = isAllowGrid ? allowHeaderNames_HRM3110_R01 : deductHeaderNames_HRM3110_R01;
    const columnStateKey = isAllowGrid ? ALLOW_COLUMN_STATE_KEY : DEDUCT_COLUMN_STATE_KEY;

    // 공통코드 Store에서 구분(AD_TYPE) 표시명과 과세구분(TAX_TYPE) 드롭다운 목록을 가져온다.
    const { adTypeList, taxTypeList } = useCommonCodeStore();

    const isRowDraggingRef = useRef(false);

    // 그리드 기본 설정: 행 ID 생성, 행·셀 CSS 규칙 생성 함수, 빈 행을 채운 표시 목록, 행 번호 칸 렌더러, 스위치 핸들러를 받는다.
    const {
        getRowId, createRowClassRules, createCellClassRules, paddedGridData,
        renderIndexCell, handleSwitchButtonHandler,
    } = useGridSettingContent<HRM3110_R01Dto>({
        gridRef,
        // 각 행을 구분하는 기준 필드
        key: 'valueIndex',
        gridData: gridList,
        defaultGridData: defaultHRM3110_R01Row,
        selectedRow: selectedList,
        originalGridData: originalList,
        updateRow: updateGridRow,
    });

    // 신규·기존 행별 비활성 셀과 포인터·정렬 스타일을 정의한다.
    // 의존성이 []이므로 처음 한 번만 만들고 같은 객체를 계속 사용한다.
    const cssCellConfig = useMemo<GridCellConfig>(() => ({
        // 모든 행에서 비활성(회색)으로 표시할 컬럼. 구분은 추가한 그리드로 정해지는 표시 전용 값이다.
        disabledCells: ['allowDeductType'],
        // 신규 행에서만 비활성으로 표시할 컬럼
        insertedDisabledCells: [],
        // 기존 행에서만 비활성으로 표시할 컬럼. 저장된 코드는 기본키라서 바꿀 수 없다.
        commonDisabledCells: ['allowDeductCd'],
        // 모든 행에서 손가락 커서를 표시할 컬럼
        pointerCells: ['allowDeductNm', 'taxType', 'ordinaryYn', 'nontaxLimit', 'salaryYn', 'bonusYn', 'useYn', 'sortSeq', 'remark'],
        // 신규 행에서만 손가락 커서를 표시할 컬럼
        insertedPointerCells: ['allowDeductCd'],
        // 기존 행에서만 손가락 커서를 표시할 컬럼
        commonPointerCells: [],
        // 왼쪽 정렬할 컬럼
        alignLeftCells: ['allowDeductCd', 'allowDeductNm', 'remark'],
    }), []);

    // 위 설정을 신규 행용·기존 행용 목록으로 합친 결과를 구조 분해로 꺼낸다.
    const {
        disabledInsertedColumns, disabledColumns, pointerInsertedColumns, pointerColumns, alignLeftColumns,
    } = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

    // 모든 컬럼에 적용할 기본 설정에 셀 CSS 규칙(선택·삭제·비활성·포인터·정렬)을 더한 객체
    // 선택·삭제 값은 문자열로 저장되므로 숫자 valueIndex와 비교할 때 숫자로 바꾼다.
    const cellCssDefaultColDef = useMemo(() => ({
        ...defaultColDef,
        cellClassRules: createCellClassRules<HRM3110_R01Dto>({
            idKey: 'valueIndex',
            selectedList: selectedList.map(Number),
            deletedList: deletedList.map(Number),
            disabledInsertedColumns, disabledColumns,
            pointerInsertedColumns, pointerColumns, alignLeftColumns,
        }),
    }), [createCellClassRules, selectedList, deletedList,
        disabledInsertedColumns, disabledColumns, pointerInsertedColumns, pointerColumns, alignLeftColumns]);

    // 클릭·키보드로 편집을 시작할 수 있는 컬럼 목록. 공제 그리드에는 비과세한도 컬럼이 없다.
    // 의존성: 그리드 종류가 바뀔 때만 다시 만든다.
    const editableColumns = useMemo(() => isAllowGrid
        ? ['allowDeductCd', 'allowDeductNm', 'taxType', 'nontaxLimit', 'sortSeq', 'remark']
        : ['allowDeductCd', 'allowDeductNm', 'taxType', 'sortSeq', 'remark'], [isAllowGrid]);
    // 드롭다운에서 Enter/Tab을 누르면 다음 편집 가능 셀로 포커스를 옮긴다.
    const handleGridDropDownCommitAfter = useMemo(
        () => createGridDropDownCommitAfter<HRM3110_R01Dto>(editableColumns),
        [editableColumns],
    );

    /**
     * 행 번호 칸의 - 버튼 동작이다. 저장된 시스템 항목은 경고만 하고, 그 외 행은 공통 삭제 처리에 넘긴다.
     * 공통 RemoveRowHandler는 삭제 콜백보다 먼저 그리드에서 행을 지우므로 시스템 항목은 호출 전에 막아야 한다.
     * @param rowData - 버튼을 누른 행
     * @returns 반환값은 없다.
     */
    const handleRemoveRow = useCallback((rowData: HRM3110_R01Dto) => {
        // 저장된 시스템 항목은 계산 로직이 쓰는 코드라 삭제 표시하지 않는다.
        if (!rowData.inserted && isSystemAllowDeductCode(rowData.allowDeductCd)) {
            // 같은 클릭이 행 선택으로 처리되지 않도록 한 번 무시하게 한다.
            setClickable(false);
            showWarning('시스템 항목은 삭제할 수 없습니다. 사용하지 않으려면 사용을 N으로 바꾸세요.');
            return;
        }
        // 공통 함수가 마지막 1행 삭제를 막고, 신규 행은 즉시 제거·기존 행은 문자열 식별값으로 삭제 표시한다.
        RemoveRowHandler(gridRef, gridList, rowData, setClickable, removeGridRow, addDeleted);
    }, [gridRef, gridList, setClickable, removeGridRow, addDeleted]);

    // 구분·과세구분·Y/N·순서·비고는 두 그리드가 같고, 통상임금·비과세한도는 지급 그리드에만 있다.
    const baseColumnDefs = useMemo<ColDef<HRM3110_R01Dto>[]>(() => [
        // 펼치기(...): 행 번호 컬럼 설정 배열의 항목을 이 배열 안에 그대로 풀어 넣는다.
        ...getDefaultRowIndexColumnConfig(
            // 행 번호 칸 렌더러: 번호 또는 D·I·U 표시와 +/- 버튼을 그린다.
            (params: HRM3110_R01Dto) => renderIndexCell(
                params,
                // 행 상태 판단: 공통 함수가 삭제·선택·신규·수정 여부를 계산해 반환한다.
                (innerParams: HRM3110_R01Dto) => getRowStyleFlags(
                    innerParams,
                    {
                        deletedList,
                        selectedList,
                        dataList: gridList,
                    }),
                // + 버튼: 해당 행 아래에 신규 행을 추가한다. Ctrl을 누른 채 클릭하면 10행을 한 번에 추가한다.
                (rowIndex: number, e: React.MouseEvent) => {
                    if (e.ctrlKey) {
                        for (let i = 0; i < 10; i++) { handleInsertClick(e, rowIndex, gridType); }
                    } else {
                        handleInsertClick(e, rowIndex, gridType);
                    }
                },
                // - 버튼: 시스템 항목을 막은 뒤 공통 삭제 처리를 호출한다. rowData는 renderIndexCell에서 Dto 형식으로 선언함
                handleRemoveRow,
            ),
        ),
        {
            headerName: headerNames.allowDeductCd, colId: 'allowDeductCd', field: 'allowDeductCd', width: 90,
            // 이미 저장된 행의 기본키는 편집하지 않는다.
            editable: params => params.data?.inserted === true,
            pasteable: true,
            // 코드는 DB 컬럼 길이 20자까지 입력받는다.
            cellEditor: 'agTextCellEditor', cellEditorParams: { maxLength: 20 },
            // ??: 값이 null·undefined이면 빈 문자열을 쓴다. 앞뒤 공백을 지우고 대문자로 바꿔 저장한다.
            valueParser: params => String(params.newValue ?? '').trim().toUpperCase(),
        },
        {
            headerName: headerNames.allowDeductNm, colId: 'allowDeductNm', field: 'allowDeductNm', width: 150,
            editable: true, pasteable: true,
            // 항목명은 DB 컬럼 길이 50자까지 입력받는다.
            cellEditor: 'agTextCellEditor', cellEditorParams: { maxLength: 50 },
        },
        {
            headerName: headerNames.allowDeductType, colId: 'allowDeductType', field: 'allowDeductType', width: 80,
            // 구분은 추가한 그리드로 정해지므로 편집하지 않고 AD_TYPE 코드명으로만 보여 준다.
            editable: false,
            valueFormatter: params => adTypeList.find(item => item.minorCd === params.value)?.codeNm ?? params.value ?? '',
        },
        {
            headerName: headerNames.taxType, colId: 'taxType', field: 'taxType', width: 100,
            // 공통 그리드 드롭다운을 사용해 코드명으로 보여 주고 minorCd를 저장한다.
            ...CommonCodeColumn.options<HRM3110_R01Dto>(
                taxTypeList,
                // 모든 행에서 편집 가능
                true,
                CommonCodeColumn.change(updateGridRow, {
                    // 비과세(N)에서 다른 과세구분으로 바꾸면 비과세한도를 0으로 함께 되돌린다.
                    // 삼항 연산자: 비과세이면 추가 변경 없이 빈 패치를, 그 외 과세구분이면 비과세한도 0 패치를 반환한다.
                    getPatch: ({ newValue }) => newValue === TAX_TYPE_NONTAX ? {} : { nontaxLimit: 0 },
                }),
                handleGridDropDownCommitAfter,
            ),
        },
        // 펼치기(...): 지급 그리드일 때만 통상임금·비과세한도 컬럼을 이 위치에 넣는다.
        ...(isAllowGrid ? [
            {
                headerName: headerNames.ordinaryYn, colId: 'ordinaryYn', field: 'ordinaryYn', width: 80,
                // 통상임금 포함 여부 스위치. 마지막 true는 모든 행에서 조작 가능하다는 뜻이다.
                ...getSwitchButtonColumnOptions<HRM3110_R01Dto>('ordinaryYn', handleSwitchButtonHandler,
                    headerNames, aggridSwitchButtonWrapperStyle, true),
            },
            {
                headerName: headerNames.nontaxLimit, colId: 'nontaxLimit', field: 'nontaxLimit', width: 110,
                // 과세구분이 비과세(N)인 행만 비과세한도를 입력할 수 있다.
                editable: params => params.data?.taxType === TAX_TYPE_NONTAX,
                // 입력값에서 쉼표·공백을 제거하고 숫자로 바꾼다. 숫자가 아니면 0을 반환한다.
                // 정규식 /,/g: 문자열 안의 모든 쉼표를 찾는다.
                valueParser: params => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
                // 하단 합계 행은 비워 두고, 데이터 행은 천 단위 쉼표를 붙여 표시한다.
                valueFormatter: params => params.node?.rowPinned ? '' : (params.value?.toLocaleString() ?? '0'),
                // 비과세가 아닌 행은 비과세한도 칸을 회색으로 표시한다.
                cellClassRules: {
                    'disabled-cell': params => !params.node.rowPinned && params.data?.taxType !== TAX_TYPE_NONTAX,
                },
            },
        ] as ColDef<HRM3110_R01Dto>[] : []),
        {
            headerName: headerNames.salaryYn, colId: 'salaryYn', field: 'salaryYn', width: 80,
            // 지급은 급여대상, 공제는 급여공제 스위치이다. 같은 salary_yn 컬럼을 쓴다.
            ...getSwitchButtonColumnOptions<HRM3110_R01Dto>('salaryYn', handleSwitchButtonHandler,
                headerNames, aggridSwitchButtonWrapperStyle, true),
        },
        {
            headerName: headerNames.bonusYn, colId: 'bonusYn', field: 'bonusYn', width: 80,
            // 지급은 상여대상, 공제는 상여공제 스위치이다. 같은 bonus_yn 컬럼을 쓴다.
            ...getSwitchButtonColumnOptions<HRM3110_R01Dto>('bonusYn', handleSwitchButtonHandler,
                headerNames, aggridSwitchButtonWrapperStyle, true),
        },
        {
            headerName: headerNames.useYn, colId: 'useYn', field: 'useYn', width: 70,
            // 사용 여부 스위치
            ...getSwitchButtonColumnOptions<HRM3110_R01Dto>('useYn', handleSwitchButtonHandler,
                headerNames, aggridSwitchButtonWrapperStyle, true),
        },
        {
            headerName: headerNames.sortSeq, colId: 'sortSeq', field: 'sortSeq', width: 70, editable: true,
            // 입력 순서에서 쉼표·공백을 제거하고 숫자로 바꾼다. 숫자가 아니면 0을 반환한다.
            valueParser: params => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
        },
        {
            headerName: headerNames.remark, colId: 'remark', field: 'remark', width: 320, editable: true, pasteable: true,
            // 기본 텍스트 편집기를 쓰고 최대 1000자까지 입력받는다.
            cellEditor: 'agTextCellEditor', cellEditorParams: { maxLength: 1000 },
        },
    ], [renderIndexCell, deletedList, selectedList, gridList, handleInsertClick, gridType,
        handleRemoveRow, headerNames, adTypeList,
        taxTypeList, updateGridRow, handleGridDropDownCommitAfter, isAllowGrid, handleSwitchButtonHandler]);

    // 기본 컬럼 순서를 생성하고 저장된 컬럼 상태가 있을 때만 그 상태를 적용한다.
    const defaultColumnState = useMemo(() => buildDefaultColumnStateFromDefs(baseColumnDefs, []), [baseColumnDefs]);
    // 삼항 연산자: columnState가 없으면 기본 정의를, 있으면 사용자가 저장한 배치를 반영한 정의를 반환한다.
    const columnDefs = useMemo(() => columnState === null ? baseColumnDefs : GridCustomColumnDefs<HRM3110_R01Dto>({
        baseColumnDefs, columnState, headerNames,
        fallbackHeaderClass: aggridHeaderClass,
    }), [baseColumnDefs, columnState, headerNames]);

    // 셀 값이 바뀌었을 때 이 그리드의 Store 목록에 반영하는 함수를 가져온다.
    const { handleCellChange } = useHRM3110_R01CellChange({ updateRow: updateGridRow });
    // 행 클릭 선택 토글 핸들러: 공통 Hook이 합계 행·삭제 행을 제외하고 선택을 추가·해제한다.
    // 공통 Hook은 valueIndex를 숫자 그대로 저장하므로, 선택 목록에는 숫자(행 클릭)와 문자열(전체 선택)이 함께 들어올 수 있다. (HRM2130_R01과 동일)
    const handleRowClick = useRowSelectionHandler({
        dataList: gridList,
        idKey: 'valueIndex',
        selectedList,
        deletedList,
        addSelected,
        removeSelected,
        clickable, setClickable,
    });

    // 방향키 이동과 편집 가능 컬럼 내 키보드 탐색을 공통 이벤트 훅에 연결한다.
    const cellEventHandlers = useDetailTableEvents({
        detailList: gridList, editableColumns,
        keyDownConfig: { enableArrowNavigation: true },
    });

    // 셀 클릭 동작 목록: 편집 가능 컬럼을 클릭하면 셀 편집을 시작한다.
    const cellClickActions = useMemo<CustomCellClickAction<HRM3110_R01Dto>[]>(() => [{
        // 코드 칸은 신규 행일 때만, 비과세한도 칸은 비과세 행일 때만 true를 반환한다.
        validate: (field, data) => editableColumns.includes(field) &&
            (field !== 'allowDeductCd' || data.inserted) &&
            (field !== 'nontaxLimit' || data.taxType === TAX_TYPE_NONTAX),
        // 유효한 클릭의 셀 편집을 시작한다. 반환값은 없다.
        // !: rowIndex와 field가 반드시 있다고 TypeScript에 알려 준다.
        action: event => event.api.startEditingCell({ rowIndex: event.rowIndex!, colKey: event.colDef.field! }),
    }], [editableColumns]);

    // 클릭한 셀에 맞는 동작을 찾아 실행하는 셀 클릭 핸들러
    const handleCellClick = CreateCellClickHandler(gridList, 'valueIndex', cellClickActions);
    /**
     * 헤더 정렬 후 원본·현재 행 인덱스를 기준으로 표시 순서를 맞춘다.
     * @param params 정렬된 행 정보
     * @returns 반환값은 없다.
     */
    const handlePostSortWithDrag = useCallback((params: PostSortRowsParams<HRM3110_R01Dto>) => {
        if (isRowDraggingRef.current) return;
        postSortHandler(params, gridList, originalList, setGridList);
    }, [gridList, originalList, setGridList]);

    // 하단 고정 행의 코드 컬럼에 현재 그리드 건수를 표시한다.
    const pinnedBottomRow = useMemo(() => [{
        allowDeductCd: gridList.length.toLocaleString() + '건',
        editable: false,
    }], [gridList]);

    // 계산·선택·정렬·편집 이벤트와 컬럼 상태를 연결한 JSX를 반환한다.
    return (
        // 우클릭 메뉴(전체 선택·선택 해제), 셀 계산, 컬럼 상태 저장을 담당하는 공통 감싸기 컴포넌트
        <GridContextWrapper<HRM3110_R01Dto>
            enableCellCalculation
            gridRef={gridRef}
            paddedGridData={paddedGridData}
            initSelected={initSelected}
            defaultRowData={defaultHRM3110_R01Row}
            allSelected={allSelected}
            baseColumnDefs={columnDefs}
            defaultColumnState={defaultColumnState}
            setColumnState={setColumnState}
            localStorageKey={columnStateKey}
            menuItems={undefined}
        >
            {/* 공통 제목은 지급 그리드이면 지급항목(수당), 아니면 공제항목을 표시한다. */}
            <SmallTitleWithSubSpan title={isAllowGrid ? '지급항목 (수당)' : '공제항목'} />
            {/* 실제 편집 그리드: 행 데이터, 컬럼 정의, 행·셀 CSS 규칙, 클릭·키보드·값 변경·정렬 이벤트를 연결한다. */}
            <Grid_Excel<HRM3110_R01Dto>
                ref={gridRef}
                rowData={paddedGridData}
                getRowId={getRowId}
                columnDefs={columnDefs}
                defaultColDef={cellCssDefaultColDef}
                rowClassRules={createRowClassRules<HRM3110_R01Dto>({
                    idKey: 'valueIndex',
                    selectedList: selectedList.map(Number),
                    deletedList: deletedList.map(Number),
                })}
                onRowClicked={handleRowClick}
                onCellClicked={handleCellClick}
                onCellKeyDown={cellEventHandlers.handleCellKeyDown}
                onCellValueChanged={handleCellChange}
                pinnedBottomRowData={pinnedBottomRow}
                onPostSortRows={handlePostSortWithDrag}
                columnStateKey={columnStateKey}
                setColumnState={setColumnState}
                getDefaultColumnState={() => defaultColumnState}
                scrollTop={scrollTop}
                setScrollTop={setScrollTop}
            />
        </GridContextWrapper>
    );
};
